import React from "react";
import { Control, FieldErrors } from "react-hook-form";
import {
    mapToDropdownOption,
    ensure,
    FormSingleSelectList,
    DropdownOption,
    Stack,
    mapToCodedProperty,
    ValidationResult,
    getById
} from "sprint-common-web";

import { SuggestContentListByTitleResponseMovie } from "../../../../../common/api/catalogue-api/contents-api/SuggestContentListByTitleResponse";
import { CreateContentVersionButton } from "../../../../../common/components/content-version/CreateContentVersionButton";
import { CrossTypeContentSearchItem } from "../../../../../common/components/form/CrossTypeContentSearch";
import { ContentVersion } from "../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { isTrailerVersionType } from "../../../../../common/types/TrailerContentVersion";
import { renderBrandedContentVersionText } from "../../../../../common/utils/renderContentVersionText";
import { ensureMovieAssociateDetails } from "../../../../../content-ingestion/common/content/types/ContentAssociationDetails";
import { renderTrailerVersionInfo } from "../../../../../content-ingestion/common/content/utils/renderTrailerVersionInfo";
import { useCreateOrder } from "../../common/provider/useCreateOrder";
import { OrderMovieVersionItem } from "../../types/OrderContentVersionItem";
import { createOrderMovieItemValidator } from "./OrderMovieItemValidator";

interface OrderMovieVersionSelectorProps {
    control: Control;
    errors: FieldErrors;
    dependsOn: string[];
}

export const OrderMovieVersionSelector: React.FC<OrderMovieVersionSelectorProps> = ({
    control,
    errors,
    dependsOn
}: OrderMovieVersionSelectorProps) => {
    const { contentDetails, setMovieVersion, setContentDetails } = useCreateOrder();
    const dropdownOptions = getDropdownOptions(contentDetails);

    const validator = createOrderMovieItemValidator();

    const validateVersion = (itemId: string): ValidationResult => {
        ensure(contentDetails);
        ensureMovieAssociateDetails(contentDetails);

        const versions = contentDetails?.versions || [];

        const versionToValidate = getById(versions, itemId);

        return validator.validateVersion(versionToValidate, versions);
    };

    const onContentVersionSelect = (selectedVersionId: string) => {
        ensure(contentDetails);
        ensureMovieAssociateDetails(contentDetails);
        const selectedVersion = contentDetails.versions.find((v) => v.id === selectedVersionId);

        if (!selectedVersion) {
            setMovieVersion(undefined);
            return;
        }

        setMovieVersion(
            getSetMovieVersionParams({
                contentDetails,
                version: {
                    distributor: {
                        id: selectedVersion.distributor.id,
                        name: selectedVersion.distributor.name
                    },
                    brandings: selectedVersion.brandings?.map((item) => mapToCodedProperty(item)),
                    id: selectedVersion.id,
                    type: selectedVersion.type,
                    buzzId: selectedVersion.buzzVersion?.id
                }
            })
        );
    };

    const shouldRenderTrailerInfo = (itemId: string) => {
        ensure(contentDetails);
        ensureMovieAssociateDetails(contentDetails);

        if (!contentDetails?.versions.length) return false;

        const versionToCheck = contentDetails.versions.find((v) => v.id === itemId);

        return isTrailerVersionType(versionToCheck?.type);
    };

    const renderAdditionalInfo = (itemId: string) => {
        if (!shouldRenderTrailerInfo(itemId)) {
            return <></>;
        }

        return renderTrailerVersionInfo({});
    };

    const createContentVersionButtonView = contentDetails && (
        <Stack style={{ marginLeft: "5px" }}>
            <CreateContentVersionButton
                content={{
                    type: ContentReferenceType.Movie,
                    movieId: contentDetails.id
                }}
                onContentVersionCreated={(newVersion) => {
                    ensureMovieAssociateDetails(contentDetails);

                    setContentDetails({
                        ...contentDetails,
                        versions: [newVersion, ...contentDetails.versions]
                    });

                    setMovieVersion(
                        getSetMovieVersionParams({
                            contentDetails,
                            version: {
                                distributor: {
                                    id: newVersion.distributor.id,
                                    name: newVersion.distributor.name
                                },
                                brandings: newVersion.brandings?.map((item) => mapToCodedProperty(item)),
                                id: newVersion.id,
                                type: newVersion.type
                            }
                        })
                    );
                }}
            />
        </Stack>
    );

    const actionButtons: JSX.Element[] = [];

    if (createContentVersionButtonView) {
        actionButtons.push(createContentVersionButtonView);
    }

    return (
        <>
            <FormSingleSelectList
                items={dropdownOptions}
                onChange={onContentVersionSelect}
                onItemValidating={validateVersion}
                renderAdditionalInfo={renderAdditionalInfo}
                control={control}
                errors={errors}
                rules={{ required: { value: true, message: "Select version" } }}
                actionButtons={actionButtons}
                name="contentVersionId"
                label="Version"
                disabled={!dropdownOptions.length}
                minHeight={150}
                maxHeight={150}
                dependsOn={dependsOn}
                testId="create-order-movie-selector"
            />
        </>
    );
};

const getDropdownOptions = (contentDetails?: CrossTypeContentSearchItem): DropdownOption[] => {
    if (!contentDetails) {
        return [];
    }

    ensureMovieAssociateDetails(contentDetails);

    return contentDetails.versions.map((opt) => mapToDropdownOption(opt, () => renderBrandedContentVersionText(opt)));
};

const getSetMovieVersionParams = ({
    contentDetails,
    version
}: {
    version: ContentVersion;
    contentDetails: SuggestContentListByTitleResponseMovie;
}): OrderMovieVersionItem => ({
    type: ContentReferenceType.Movie,
    subtype: contentDetails.subtype,
    movie: {
        id: contentDetails.id,
        title: contentDetails.title,
        buzzId: contentDetails.buzz?.id
    },
    version
});
